<template>
  <div class="offcanvas offcanvas-end" style="width: min(640px, 100vw);" id="OffcanvasPriceBook" ref="offcanvas" data-bs-backdrop="static">
    <div class="bg-body-secondary border offcanvas-header py-2">
      <span>
        <span class="badge me-2" :style="{ backgroundColor: platform.color || '#6B7280' }">{{ platform.name }}</span>
        {{ $t('_delivery.Platform prices') }}
      </span>
      <button type="button" style="font-size: small;" class="btn-close shadow-none text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="border-bottom px-3 py-2 d-flex flex-wrap align-items-center gap-2">
        <div class="d-flex align-items-center gap-2">
          <label class="form-label mb-0 small fw-bold">{{ $t('_delivery.Price scope') }}</label>
          <select class="form-select form-select-sm w-auto shadow-none" v-model="branchId" @change="fetchData">
            <option :value="null">{{ $t('_delivery.All branches (central price)') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div class="input-group input-group-sm w-auto ms-auto">
          <input
            type="search"
            class="form-control shadow-none"
            :placeholder="$t('search')+'...'"
            v-model.trim="search"
          >
          <span class="input-group-text"><i class="fas fa-search"/></span>
        </div>
      </div>

      <!-- เครื่องมือเติมราคาครั้งเดียว: ราคาหน้าร้าน +X% ปัดขึ้นหน่วย Y บาท (FE ล้วน — ผลลัพธ์เป็นแถว explicit) -->
      <div class="border-bottom px-3 py-2 bg-light d-flex flex-wrap align-items-end gap-2">
        <div>
          <label class="form-label mb-0" style="font-size: 12px;">{{ $t('_delivery.Fill from store price') }} +%</label>
          <input type="number" min="0" step="1" class="form-control form-control-sm shadow-none" style="width: 90px;" v-model.number="fill.markup">
        </div>
        <div>
          <label class="form-label mb-0" style="font-size: 12px;">{{ $t('_delivery.Round up to (baht)') }}</label>
          <input type="number" min="1" step="1" class="form-control form-control-sm shadow-none" style="width: 90px;" v-model.number="fill.roundTo">
        </div>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="applyFill(false)">
          {{ $t('_delivery.Fill empty rows') }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="applyFill(true)">
          {{ $t('_delivery.Refill all rows') }}
        </button>
        <div class="text-muted w-100" style="font-size: 12px;">
          {{ $t('_delivery.Empty price = sold at store price - only rows you fill are saved') }}
        </div>
      </div>

      <div class="px-3 pb-3">
        <template v-for="category in filteredCategories" :key="category.id">
          <div class="fw-bold border-bottom py-2 mt-2">{{ $ln(category) }}</div>
          <table class="table table-sm align-middle mb-0">
            <tbody>
              <template v-for="product in category.products" :key="product.product_id">
                <tr v-if="!product.is_config">
                  <td>{{ $ln(product) }}</td>
                  <td class="text-end text-muted" style="width: 110px;">
                    {{ formatStorePrice(product) }}
                  </td>
                  <td style="width: 130px;">
                    <input
                      type="number"
                      min="0"
                      step="0.25"
                      class="form-control form-control-sm text-end shadow-none"
                      :class="{ 'border-primary fw-bold': rowValue(product.product_id) !== '' }"
                      :placeholder="placeholderFor(product)"
                      :value="rowValue(product.product_id)"
                      @input="setRowValue(product.product_id, $event.target.value)"
                    >
                  </td>
                </tr>
                <template v-else>
                  <tr>
                    <td colspan="3" class="text-muted small">{{ $ln(product) }}</td>
                  </tr>
                  <tr v-for="variant in product.variants" :key="variant.product_id">
                    <td class="ps-4">{{ $ln(variant) }}</td>
                    <td class="text-end text-muted" style="width: 110px;">
                      {{ formatStorePrice(variant) }}
                    </td>
                    <td style="width: 130px;">
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        class="form-control form-control-sm text-end shadow-none"
                        :class="{ 'border-primary fw-bold': rowValue(variant.product_id) !== '' }"
                        :placeholder="placeholderFor(variant)"
                        :value="rowValue(variant.product_id)"
                        @input="setRowValue(variant.product_id, $event.target.value)"
                      >
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </template>
      </div>
    </div>
    <div class="bg-light border-top justify-content-center modal-footer py-2" v-if="!loading && hasPermission">
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="!dirty"
        @click="handleSave"
      />
      <button type="button" data-bs-dismiss="offcanvas" class="border-dark-subtle btn-sm btn btn-light me-2 ms-2">
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { DeliveryPlatforms } from '@/services/endpoint/delivery-platforms.service';

/**
 * price book ราคาขายบน platform (docs ฝั่ง api: delivery-platform-pricing.md)
 * - ช่องว่าง = ขายราคาหน้าร้าน (ไม่มีแถวในตาราง) / ช่องมีค่า = ราคา explicit
 * - scope: ราคากลาง (ทุกสาขา) หรือ exception รายสาขา — บันได: สาขา → กลาง → หน้าร้าน
 * - save ส่งเฉพาะแถวที่แก้ (dirty) — ลบค่า = ส่ง null = ลบแถวกลับไปใช้บันไดล่าง
 */
export default {
  props: {
    platform: { type: Object, required: true },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      branchId: null,
      branches: [],
      categories: [],
      search: '',
      fill: { markup: 30, roundTo: 5 },
      // ค่าในช่อง (string) ต่อ product_id + snapshot ต้นฉบับไว้เทียบ dirty
      values: {},
      original: {},
    }
  },
  components: { PlaceholderContent, Ladda, AccessDenied },
  computed: {
    dirty() {
      return Object.keys(this.values).some(id => (this.values[id] ?? '') !== (this.original[id] ?? ''));
    },
    filteredCategories() {
      if (!this.search) return this.categories;
      const q = this.search.toLowerCase();
      return this.categories
        .map(c => ({
          ...c,
          products: c.products.filter(p =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.variants || []).some(v => (v.name || '').toLowerCase().includes(q))
          )
        }))
        .filter(c => c.products.length);
    }
  },
  methods: {
    // ราคาหน้าร้านที่ effective ใน scope นี้ (สาขา override → base)
    storePrice(row) {
      const branchPrice = row.branch_price;
      return (branchPrice !== null && branchPrice !== undefined)
        ? parseFloat(branchPrice)
        : (parseFloat(row.base_price) || 0);
    },
    formatStorePrice(row) {
      return this.$storeIndex.formatThaiCurrency(this.storePrice(row));
    },
    // placeholder ของช่องว่าง: scope สาขา → โชว์ราคากลางถ้ามี (บันไดชั้นถัดไป) ไม่งั้นราคาหน้าร้าน
    placeholderFor(row) {
      if (this.branchId && row.platform_central_price !== null && row.platform_central_price !== undefined) {
        return parseFloat(row.platform_central_price).toFixed(2);
      }
      return this.storePrice(row).toFixed(2);
    },
    rowValue(productId) {
      return this.values[productId] ?? '';
    },
    setRowValue(productId, value) {
      this.values[productId] = value;
    },
    eachRow(callback) {
      for (const category of this.categories) {
        for (const product of category.products) {
          if (product.is_config) {
            for (const variant of (product.variants || [])) callback(variant);
          } else {
            callback(product);
          }
        }
      }
    },
    applyFill(overwrite) {
      const markup = 1 + (Number(this.fill.markup) || 0) / 100;
      const roundTo = Math.max(1, Number(this.fill.roundTo) || 1);
      this.eachRow(row => {
        const current = this.values[row.product_id] ?? '';
        if (!overwrite && current !== '') return;
        const base = this.storePrice(row);
        if (!base) return;
        const price = Math.ceil((base * markup) / roundTo) * roundTo;
        this.values[row.product_id] = String(price);
      });
    },
    async fetchData() {
      this.loading = true;
      const qs = this.branchId ? ('?' + $.param({ branch_id: this.branchId })) : '';
      await DeliveryPlatforms.getProducts(this.platform.id, qs).then(res => {
        this.categories = res.data.data?.categories ?? [];
        this.branches = res.data.branches ?? this.branches;
        this.values = {};
        this.original = {};
        this.eachRow(row => {
          // scope สาขา = แก้แถว platform_branch_price / scope กลาง = แถว platform_central_price
          const raw = this.branchId ? row.platform_branch_price : row.platform_central_price;
          const val = (raw !== null && raw !== undefined) ? String(parseFloat(raw)) : '';
          this.values[row.product_id] = val;
          this.original[row.product_id] = val;
        });
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
        else if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      });
      this.loading = false;
    },
    async handleSave() {
      if (this.loadingSave) return;
      this.loadingSave = true;
      // ส่งเฉพาะแถว dirty — ค่าว่าง = null = ลบแถว (กลับไปใช้ราคาหน้าร้าน)
      const items = [];
      for (const id of Object.keys(this.values)) {
        const val = this.values[id] ?? '';
        if (val === (this.original[id] ?? '')) continue;
        items.push({ product_id: Number(id), price: val === '' ? null : Number(val) });
      }
      await DeliveryPlatforms.updateProducts(this.platform.id, {
        branch_id: this.branchId,
        items
      }).then(() => {
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        this.fetchData();
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
      }).finally(() => {
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      await this.fetchData();
    });
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.priceBook.open = false;
    });
  }
}
</script>
